import { type ReactNode, useEffect } from "react";
import { useConfigContext } from "../../../ConfigProvider.tsx";
import { useAutocomplete } from "../../providers/AutocompleteProvider.tsx";
import { createPortal } from "react-dom";

type AutocompleteModalProps = {
    children: ReactNode;
};

export function Modal({ children }: AutocompleteModalProps) {
    const { isModalOpen, setIsModalOpen, autocomplete, inputRef } = useAutocomplete();
    const { tc } = useConfigContext();

    const handleClose = () => {
        autocomplete.setQuery("");
        autocomplete.setIsOpen(false);
        setIsModalOpen(false);

        if (inputRef.current) {
            inputRef.current.value = "";
            inputRef.current.focus();
        }
    };

    useEffect(() => {
        const original = document.body.style.overflow;
        if (isModalOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = original;
        }
        return () => {
            document.body.style.overflow = original;
        };
    }, [isModalOpen]);

    useEffect(() => {
        if (!isModalOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") handleClose();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [isModalOpen]);

    if (!isModalOpen) return null;

    const modalEl = (
        <div
            className="algoliaAutocompleteModal is-active"
            role="dialog"
            aria-modal="true"
            aria-hidden={!isModalOpen}
            onClick={handleClose}
        >
            <div
                className="algoliaAutocompleteModal__box"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="algoliaAutocompleteModal__content">
                    <button
                        className="algoliaAutocompleteModal__close"
                        onClick={handleClose}
                        title={tc.close}
                        aria-label={tc.close}
                        type="button"
                    >
                        <i className="icon icon-close" aria-hidden={true}></i>
                    </button>
                    {children}
                </div>
            </div>
        </div>
    );

    return typeof document !== "undefined"
        ? createPortal(modalEl, getModalTarget())
        : null;

    function getModalTarget() {
        const header = document.getElementById("header");
        if (!header || !header.parentElement) {
            return document.body;
        }
        const wrapper = header.parentElement;
        let portalContainer = document.getElementById("modal-portal-container");
        if (!portalContainer) {
            portalContainer = document.createElement("div");
            portalContainer.id = "modal-portal-container";
            wrapper.insertBefore(portalContainer, header);
        }
        return portalContainer;
    }
}